import { mockLoans, mockBrokers, mockOffices, mockBrokerUsers, mockCompRequests, mockCompensations } from '../mockData';
import { PIPER_TERMS_VERSION } from '../../content/piperTerms';

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/** In-memory Piper access for mock consent round-trips. */
let mockPiperAccess = {
  tenantEnabled: true,
  brokerEnabled: true,
  effectiveEnabled: true,
  reason: null,
  termsVersion: PIPER_TERMS_VERSION,
  currentTermsVersion: PIPER_TERMS_VERSION,
  termsAcceptedAt: `${PIPER_TERMS_VERSION}T00:00:00Z`,
  termsDeclinedAt: null,
};

const BROKER_COMP_BPS = 275;

const parseLoanNumber = (raw) => {
  if (raw == null) return null;
  const digits = String(raw).replace(/\D/g, '');
  if (!digits) return null;
  const n = parseInt(digits, 10);
  return Number.isFinite(n) ? n : null;
};

/** Map mockLoans into PipelineLoanData / AePipelineLoanData-shaped rows. */
const buildMockPipelineLoans = () => mockLoans.map((loan, idx) => {
  const lastName = loan.borrower?.lastName || loan.borrowerName?.split(' ').pop() || '—';
  const firstName = loan.borrower?.firstName || loan.borrowerName?.split(' ')[0] || '';
  const borrowerNames = [firstName, lastName].filter(Boolean).join(' ');
  const stage = loan.status || 'Origination';
  const isFunded = /funded|closed/i.test(stage);
  const isOrigination = !loan.submittedDate && !isFunded;
  const loanNumber = parseLoanNumber(loan.loanNumber) ?? (1000 + idx);
  const broker = mockBrokers[idx % mockBrokers.length];
  const comp = (loan.loanAmount || 0) * (BROKER_COMP_BPS / 10000);

  return {
    loanId: String(loan.id),
    loanNumber,
    borrowerLastName: lastName,
    borrowerFirstName: firstName,
    borrowerNames,
    primaryBorrowerFullName: borrowerNames,
    propertyCity: loan.property?.city || '—',
    propertyState: loan.property?.state || '—',
    loanAmount: loan.loanAmount || 0,
    stage,
    loanPurpose: loan.loanDetails?.loanPurpose || 'Purchase',
    intendedUsage: loan.property?.occupancy || null,
    mortgageType: loan.loanDetails?.loanProduct?.mortgageType || null,
    programName: loan.loanDetails?.loanProduct?.programName || null,
    noteRate: loan.loanDetails?.loanProduct?.noteRate ?? null,
    ltvRatio: loan.loanDetails?.loanToValueRatio ?? null,
    brokerCompensation: comp,
    compensationPaidByType: 'LenderPaid',
    loanOriginator: loan.loanOfficer || null,
    lockExpirationDate: loan.lockExpiration || null,
    // Lock badge reads the whole lifecycle, so mock rows carry the request/confirm
    // beats too — a locked mock loan without them would look merely "requested".
    lockRequestedDate: loan.lockRequestedDate || loan.lockExpiration || null,
    lockConfirmedDate: loan.lockConfirmedDate || loan.lockExpiration || null,
    rateLockStatus: loan.rateLockStatus || (loan.lockExpiration ? 'Locked' : null),
    estimatedCloseDate: loan.loanDetails?.estimatedClosingDate || null,
    lastUpdated: loan.submittedDate || '2024-01-20',
    submittedToUnderwritingDate: loan.submittedDate || null,
    fundedDate: isFunded ? (loan.submittedDate || '2024-02-01') : null,
    underwriterDecision: stage === 'Approved' || stage === 'Conditions' ? 'ConditionallyApprove'
      : stage === 'Clear to Close' ? 'ClearToClose' : null,
    brokerCompanyName: broker?.name || 'Prime Mortgage Solutions',
    brokerCompanyNmls: broker?.nmls || '123456',
    creditScore: loan.borrower?.creditScore || 0,
    _bucket: isFunded ? 'Funded' : isOrigination ? 'InOrigination' : 'Submitted',
  };
});

const applyPipelineFilters = (loans, params = {}) => {
  let result = [...loans];

  if (params.view === 'InOrigination') result = result.filter((l) => l._bucket === 'InOrigination');
  else if (params.view === 'Submitted') result = result.filter((l) => l._bucket === 'Submitted');
  else if (params.view === 'Funded') result = result.filter((l) => l._bucket === 'Funded');

  if (params.stages) {
    const set = new Set(String(params.stages).split(',').map((s) => s.trim()).filter(Boolean));
    if (set.size) result = result.filter((l) => set.has(l.stage));
  }
  if (params.purpose) {
    const p = String(params.purpose).toLowerCase();
    result = result.filter((l) => (l.loanPurpose || '').toLowerCase().includes(p));
  }
  if (params.loanNumber) {
    const n = parseLoanNumber(params.loanNumber);
    if (n != null) result = result.filter((l) => l.loanNumber === n || String(l.loanNumber).includes(String(n)));
  }
  if (params.lastName) {
    const q = String(params.lastName).toLowerCase();
    result = result.filter((l) =>
      (l.borrowerLastName || '').toLowerCase().includes(q)
      || (l.borrowerNames || '').toLowerCase().includes(q)
      || (l.primaryBorrowerFullName || '').toLowerCase().includes(q));
  }
  if (params.brokerCompany) {
    const q = String(params.brokerCompany).toLowerCase();
    result = result.filter((l) =>
      (l.brokerCompanyName || '').toLowerCase().includes(q)
      || (l.brokerCompanyNmls || '').toLowerCase().includes(q));
  }

  const sortBy = (params.sortBy || 'lastUpdated').toLowerCase();
  const desc = String(params.sortDirection || 'desc').toLowerCase() === 'desc';
  result.sort((a, b) => {
    let av = a[sortBy];
    let bv = b[sortBy];
    // Common aliases
    if (sortBy === 'lastname' || sortBy === 'borrowerlastname') { av = a.borrowerLastName; bv = b.borrowerLastName; }
    if (sortBy === 'purpose' || sortBy === 'loanpurpose') { av = a.loanPurpose; bv = b.loanPurpose; }
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    if (av < bv) return desc ? 1 : -1;
    if (av > bv) return desc ? -1 : 1;
    return 0;
  });

  return result;
};

const paginate = (items, params = {}) => {
  const page = Math.max(1, parseInt(params.page, 10) || 1);
  const pageSize = Math.min(100, Math.max(1, parseInt(params.pageSize, 10) || 25));
  const start = (page - 1) * pageSize;
  return {
    items: items.slice(start, start + pageSize).map(({ _bucket, ...rest }) => rest),
    totalCount: items.length,
    page,
    pageSize,
  };
};

const gridCounts = (all) => ({
  inOriginationCount: all.filter((l) => l._bucket === 'InOrigination').length,
  submittedCount: all.filter((l) => l._bucket === 'Submitted').length,
  fundedCount: all.filter((l) => l._bucket === 'Funded').length,
  activeCompensation: all
    .filter((l) => l._bucket !== 'Funded')
    .reduce((sum, l) => sum + (l.brokerCompensation || 0), 0),
});

const buildPagedPipelineResponse = (params = {}) => {
  const all = buildMockPipelineLoans();
  // Ensure at least one origination loan for UI demos
  if (!all.some((l) => l._bucket === 'InOrigination') && all.length > 0) {
    all[all.length - 1]._bucket = 'InOrigination';
    all[all.length - 1].submittedToUnderwritingDate = null;
    all[all.length - 1].stage = 'Origination';
  }
  const filtered = applyPipelineFilters(all, params);
  const page = paginate(filtered, params);
  const counts = gridCounts(all);
  return { ...page, ...counts };
};

const buildPagedAeLoansResponse = (params = {}) => {
  const all = buildMockPipelineLoans();
  const filtered = applyPipelineFilters(all, params);
  return paginate(filtered, params);
};

const buildAePipelineAggregates = () => {
  const loans = buildMockPipelineLoans().map(({ _bucket, ...rest }) => rest);
  const totalAmt = loans.reduce((s, l) => s + (l.loanAmount || 0), 0);
  return {
    loans,
    summary: {
      totalActiveLoans: loans.length,
      pipelineVolume: totalAmt,
      avgLoanAmount: loans.length ? Math.round(totalAmt / loans.length) : 0,
      weightedAvgCoupon: 6.875,
      weightedAvgDti: 36,
      avgFico: 740,
      submissionsMtd: 4,
      submissionsToday: 1,
      locksMtd: 3,
      locksToday: 0,
      fundingsMtd: 1,
      fundingsToday: 0,
      pullThroughRatio: 33.3,
      activeBrokers: mockBrokers.length,
      avgDaysToClose: 28,
      locksExpiringIn3Days: 1,
      locksExpiringIn7Days: 2,
      locksExpired: 0,
      uwApprovedWithConditions: 2,
      uwClearToClose: 1,
      uwSuspended: 0,
      uwDenied: 0,
      staleLoans: 0,
      totalCompensation: totalAmt * (BROKER_COMP_BPS / 10000),
    },
    statusBreakdown: {
      submitted: 2,
      inReview: 2,
      approved: 1,
      locked: 1,
      funded: 0,
      other: 0,
    },
    productMix: {
      conventional: 4,
      fha: 1,
      va: 1,
      usda: 0,
      jumbo: 0,
      otherType: 0,
      purchase: 5,
      refinance: 1,
      otherPurpose: 0,
    },
    activityTrend: {
      days: Array.from({ length: 7 }, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - (6 - i));
        return { date: d.toISOString(), submissions: i % 3, locks: i % 2, fundings: i === 6 ? 1 : 0 };
      }),
    },
    brokerLeaderboard: mockBrokers.map((b, i) => ({
      brokerCompanyName: b.name,
      brokerCompanyNmls: b.nmls,
      volume: 400000 * (3 - i),
      loanCount: 3 - i,
      avgLoanSize: 400000,
      locksMtd: 2 - i,
      fundingsMtd: i === 0 ? 1 : 0,
    })),
  };
};

const buildPipelineStages = () => {
  const stages = [...new Set(buildMockPipelineLoans().map((l) => l.stage).filter(Boolean))].sort();
  return stages.map((s) => ({ value: s, label: s }));
};

// Route map: URL pattern → mock response
const mockRoutes = [
  { match: (url) => url === '/pipeline' || url.startsWith('/pipeline?'), handler: (_url, params) => buildPagedPipelineResponse(params) },
  { match: (url) => url === '/pipeline/stages', handler: () => buildPipelineStages() },
  { match: (url) => url === '/retail-pipeline' || url.startsWith('/retail-pipeline?'), handler: (_url, params) => buildPagedAeLoansResponse(params) },
  { match: (url) => url === '/ae-pipeline/loans' || url.startsWith('/ae-pipeline/loans?'), handler: (_url, params) => buildPagedAeLoansResponse(params) },
  { match: (url) => url === '/ae-pipeline' || url.startsWith('/ae-pipeline?'), handler: () => buildAePipelineAggregates() },
  { match: (url) => url === '/broker-companies', handler: () => mockBrokers },
  { match: (url) => /^\/broker-companies\/(\d+)$/.test(url), handler: (url) => {
    const id = parseInt(url.split('/').pop());
    return mockBrokers.find(b => b.id === id) || null;
  }},
  { match: (url) => /^\/broker-companies\/(\d+)\/offices$/.test(url), handler: (url) => {
    const id = parseInt(url.split('/')[2]);
    return mockOffices.filter(o => o.brokerId === id);
  }},
  { match: (url) => /^\/broker-companies\/(\d+)\/users$/.test(url), handler: () => mockBrokerUsers },
  { match: (url) => url === '/broker-companies/my-company', handler: () => mockBrokers[0] },
  { match: (url) => url.startsWith('/broker-companies/by-nmls'), handler: (url) => {
    const query = url.includes('?') ? url.split('?')[1] : '';
    const nmls = new URLSearchParams(query).get('companyNmls')?.trim();
    return mockBrokers.find((b) => String(b.nmls) === String(nmls || '')) || null;
  }},
  { match: (url) => url === '/broker-companies/my-company/offices', handler: () => mockOffices.filter(o => o.brokerId === 1) },
  { match: (url) => url === '/broker-companies/my-company/users', handler: () => mockBrokerUsers },
  { match: (url) => url === '/configuration/feature-flags', handler: () => ({
    scenarioPricer: true,
    mismoImportAdvisor: false,
    aeCommandCenter: true,
    lockDeskEnabled: true,
    piperAIEnabled: true,
    piperChatEnabled: true,
    piperChatQuickActionsEnabled: true,
    piperChatScenariosEnabled: true,
    piperChatFeedbackEnabled: true,
    piperPricingInsightsEnabled: true,
    piperScenarioChatEnabled: true,
  })},
  { match: (url) => url === '/configuration/piper-access', handler: () => ({ ...mockPiperAccess })},
  { match: (url) => url === '/broker-companies/account-executives', handler: () => [] },
  { match: (url) => /^\/broker-companies\/[^/]+\/compensation$/.test(url), handler: (url) => {
    const companyId = url.split('/')[2];
    return (mockCompensations[companyId] || []).map(c => ({ ...c }));
  }},
  { match: (url) => /^\/broker-companies\/[^/]+\/compensation\/active$/.test(url), handler: (url) => {
    const companyId = url.split('/')[2];
    const active = (mockCompensations[companyId] || []).find(c => c.isActive);
    return active ? { ...active } : null;
  }},
  { match: (url) => url === '/compensation-change-requests/pending', handler: () => mockCompRequests.filter(r => r.status === 'Pending').map(r => ({ ...r })) },
  { match: (url) => /^\/broker-companies\/[^/]+\/compensation\/change-requests$/.test(url), handler: (url) => {
    const companyId = url.split('/')[2];
    return mockCompRequests.filter(r => String(r.brokerCompanyId) === companyId).map(r => ({ ...r }));
  }},
];

export const mockBaseQuery = async (args) => {
  const url = typeof args === 'string' ? args : args.url;
  const method = (typeof args === 'object' ? args.method : 'GET') || 'GET';
  const params = typeof args === 'object' ? (args.params || {}) : {};

  await delay(300);

  // For mutations, return the updated object so RTK Query cache updates
  if (method !== 'GET') {
    const body = typeof args === 'object' ? args.body : null;

    if (method === 'POST' && url === '/pipeline/admin/initialize') {
      return {
        data: {
          success: true,
          loansUpserted: mockLoans.length,
          message: 'Mock pipeline cache initialized',
        },
      };
    }

    // PUT /broker-companies/{id} — update broker/office in memory and return merged object
    const brokerPutMatch = url.match(/^\/broker-companies\/([^/]+)$/);
    if (method === 'PUT' && brokerPutMatch && body) {
      const targetId = brokerPutMatch[1];
      // Find in mockBrokers or mockOffices by id (support both number and guid-style ids)
      let target = mockBrokers.find(b => String(b.id) === targetId);
      if (!target) target = mockOffices.find(o => String(o.id) === targetId);
      if (target) {
        Object.assign(target, body);
        return { data: { ...target } };
      }
    }

    // PUT /broker-companies/my-company — update the first mock broker
    if (method === 'PUT' && url === '/broker-companies/my-company' && body) {
      Object.assign(mockBrokers[0], body);
      return { data: { ...mockBrokers[0] } };
    }

    // POST /broker-companies/{id}/approve — update office and return it
    const approveMatch = url.match(/^\/broker-companies\/([^/]+)\/approve$/);
    if (method === 'POST' && approveMatch && body) {
      const targetId = approveMatch[1];
      const office = mockOffices.find(o => String(o.id) === targetId);
      if (office) {
        Object.assign(office, body, { status: 'Active' });
        return { data: { ...office } };
      }
    }

    // POST /broker-companies/{id}/compensation/change-requests — broker submits a pending request
    const submitCompReqMatch = url.match(/^\/broker-companies\/([^/]+)\/compensation\/change-requests$/);
    if (method === 'POST' && submitCompReqMatch && body) {
      const companyId = submitCompReqMatch[1];
      const company = mockBrokers.find(b => String(b.id) === companyId)
        || mockOffices.find(o => String(o.id) === companyId);
      const active = (mockCompensations[companyId] || []).find(c => c.isActive) || null;
      const request = {
        id: `cr-${Date.now()}`,
        brokerCompanyId: companyId,
        brokerCompanyName: company?.name || 'Broker Company',
        brokerCompanyNmls: company?.nmls || null,
        status: 'Pending',
        currentCompensation: active
          ? { percentage: active.percentage, dollarAmount: active.dollarAmount, minDollarAmount: active.minDollarAmount, maxDollarAmount: active.maxDollarAmount, effectiveDate: active.effectiveDate }
          : null,
        requestedCompensation: {
          percentage: body.percentage,
          dollarAmount: body.dollarAmount ?? null,
          minDollarAmount: body.minDollarAmount ?? null,
          maxDollarAmount: body.maxDollarAmount ?? null,
        },
        requestedEffectiveDate: body.effectiveDate,
        requestedByName: 'Broker Admin',
        requestedByEmail: null,
        submittedDate: new Date().toISOString(),
        approvedEffectiveDate: null,
        decidedByName: null,
        decidedDate: null,
        rejectionReason: null,
        optimalBlueAttestation: false,
      };
      mockCompRequests.unshift(request);
      return { data: { ...request } };
    }

    // DELETE /broker-companies/{id}/compensation/change-requests/{requestId}
    const cancelCompReqMatch = url.match(/^\/broker-companies\/[^/]+\/compensation\/change-requests\/([^/]+)$/);
    if (method === 'DELETE' && cancelCompReqMatch) {
      const request = mockCompRequests.find(r => String(r.id) === cancelCompReqMatch[1]);
      if (request) {
        Object.assign(request, { status: 'Cancelled' });
        return { data: { ...request } };
      }
    }

    // POST /compensation-change-requests/{id}/approve|reject
    const compDecisionMatch = url.match(/^\/compensation-change-requests\/([^/]+)\/(approve|reject)$/);
    if (method === 'POST' && compDecisionMatch) {
      const request = mockCompRequests.find(r => String(r.id) === compDecisionMatch[1]);
      if (request) {
        const now = new Date().toISOString();
        if (compDecisionMatch[2] === 'approve') {
          Object.assign(request, {
            status: 'Approved',
            approvedEffectiveDate: body?.effectiveDate || request.requestedEffectiveDate,
            optimalBlueAttestation: !!body?.optimalBlueAttestation,
            decidedByName: 'Lender Admin',
            decidedDate: now,
          });
        } else {
          Object.assign(request, {
            status: 'Rejected',
            rejectionReason: body?.reason || null,
            decidedByName: 'Lender Admin',
            decidedDate: now,
          });
        }
        return { data: { ...request } };
      }
    }

    // POST /users/me/piper-terms — record accept/decline and update access
    if (method === 'POST' && url === '/users/me/piper-terms' && body) {
      const now = new Date().toISOString();
      if (body.accepted) {
        mockPiperAccess = {
          ...mockPiperAccess,
          effectiveEnabled: true,
          reason: null,
          termsVersion: mockPiperAccess.currentTermsVersion,
          termsAcceptedAt: now,
          termsDeclinedAt: null,
        };
      } else {
        mockPiperAccess = {
          ...mockPiperAccess,
          effectiveEnabled: false,
          reason: 'consent_declined',
          termsVersion: mockPiperAccess.currentTermsVersion,
          termsAcceptedAt: null,
          termsDeclinedAt: now,
        };
      }
      return {
        data: {
          piperTermsAcceptedAt: mockPiperAccess.termsAcceptedAt,
          piperTermsDeclinedAt: mockPiperAccess.termsDeclinedAt,
          piperTermsVersion: mockPiperAccess.termsVersion,
        },
      };
    }

    return { data: body || { success: true } };
  }

  for (const route of mockRoutes) {
    if (route.match(url)) {
      const data = route.handler(url, params);
      return { data };
    }
  }

  console.warn(`[Mock API] No handler for ${method} ${url}`);
  return { data: null };
};
